<template>
	<component
		:is="'div'"
		:key="key"
		class="w-full transition-all ease-linear pointer-events-none transform-gpu scale-x-0 origin-left"
		:class="[
			barClasses ? barClasses : ' bg-primary-400 shadow-glow'
		]"
		style="height: 2px;"
		:style="{ '--tw-scale-x': progressFactor, 'opacity': show ? 1 : 0 }"
	/>
</template>

<script lang="ts">
import {
	computed, defineComponent, nextTick, PropType, ref, unref,
} from 'vue';
import { ProgressLoader } from '../composables/useProgressLoader';

export default defineComponent({
	name: 'LoadingBar',

	props: {
		progress: {
			type: [Number, Object] as PropType<number | ProgressLoader>,
			default: 0,
		},
		hideOnSuccess: {
			type: Boolean,
			default: true,
		},
		thresholdBeforeShow: {
			type: Number,
			default: 0,
		},
		barClasses: {
			type: String,
			default: null,
		},
	},

	setup(props) {
		const show = ref<boolean>(true);
		const key = ref(Date.now());
		const showTimeout = ref<ReturnType<typeof setTimeout> | null>();

		const isProgressLoader = (value: number | ProgressLoader): value is ProgressLoader => typeof value !== 'number';

		const progressFactor = computed((): number => {
			if (!isProgressLoader(props.progress)) {
				return props.progress;
			}

			const status = unref(props.progress.status);

			if (typeof status !== 'number') {
				return 0;
			}

			return status;
		});

		const onShow = () => {
			key.value = Date.now();
			show.value = true;
		};

		if (isProgressLoader(props.progress)) {
			show.value = false;

			props.progress.onStart(() => {
				if (showTimeout.value) {
					return;
				}

				if (props.thresholdBeforeShow) {
					showTimeout.value = setTimeout(onShow, props.thresholdBeforeShow);
				} else {
					onShow();
				}
			});
		}

		if (isProgressLoader(props.progress) && props.hideOnSuccess) {
			props.progress.onFinish(() => {
				if (showTimeout.value) {
					clearTimeout(showTimeout.value);
					showTimeout.value = null;
				}

				nextTick(() => {
					show.value = false;
				});
			});
		}

		return {
			show,
			key,
			progressFactor,
		};
	},
});
</script>
